<script>
  import { onMount } from "svelte";
  import {
    DataTableSkeleton,
    Select,
    SelectItem,
  } from "carbon-components-svelte";
  import { Accordion, SectionWrapper, Tooltip } from "paxray-components-svelte";
  import ButtonIconOnly from "lib/components/buttonIconOnly.svelte";
  import Header from "lib/components/Header.svelte";
  import LineChartNeu from "lib/components/LineChartNeu.svelte";
  import PaxbotChat from "lib/components/paxbot/PaxbotChat.svelte";
  import SideKpi from "lib/components/sideKPI.svelte";
  import AppSelection from "../damprojects-frontend/analysis/components/appSelection.svelte";
  import { fetchWithSession } from "lib/util/fetchWithSession.js";
  import OnGoingAnalysisPlaceholder from "./OnGoingAnalysisPlaceholder.svelte";
  import { ROUTES } from "@utils/routes.js";

  const metrics = {
    active_time_seconds: {
      label: "Annual active time",
      shortLabel: "Active time",
      formatter: formatDuration,
      chartDivisor: 3600,
    },
    users: {
      label: "Users",
      shortLabel: "Users",
      formatter: formatNumber,
      chartDivisor: 1,
    },
    copy_time_seconds: {
      label: "Annual copy time",
      shortLabel: "Copy time",
      formatter: formatDuration,
      chartDivisor: 3600,
    },
    text_time_seconds: {
      label: "Annual text time",
      shortLabel: "Text time",
      formatter: formatDuration,
      chartDivisor: 3600,
    },
    clicks: {
      label: "Clicks",
      shortLabel: "Clicks",
      formatter: formatNumber,
      chartDivisor: 1,
    },
  };
  const softwareChangeAnalysisHelpContext = {
    task: "Compare the current software state with the baseline and identify changes that need action.",
    starterQuestions: [
      "What should I investigate first?",
      "Which unwanted apps are growing?",
      "Where do Team A and Team B differ most?",
      "Where is consolidation potential?",
      "Apply the management lens to this comparison.",
    ],
  };

  let analysisView = 1;
  // The on-going analysis remains in the codebase but is temporarily not exposed.
  const sourceMode = 0;
  let loading = true;
  let error = "";
  let data = emptyAnalysis();
  let selectedMetric = "active_time_seconds";
  let baselineId = "";
  let currentId = "";
  let xTeams = [];
  let yTeams = [];
  let teamASelection = "";
  let teamBSelection = "";
  let focusedApplications = [];
  let fullscreenTeamPanel = null;
  let paxbotOpen = false;

  $: metric = metrics[selectedMetric];
  $: history = data.history || [];
  $: changes = data.changes || [];
  $: summary = data.summary || {};
  $: summaryChanges = data.summary_changes || {};
  $: comparison = data.team_comparison || {};
  $: chartDataSets = buildChartData(history, selectedMetric);
  $: decisionSignals = buildDecisionSignals(changes);
  $: xApplications = prioritizeFocusedApplications(
    buildTeamTable(comparison.applications, "x"),
    focusedApplications,
  );
  $: yApplications = prioritizeFocusedApplications(
    buildTeamTable(comparison.applications, "y"),
    focusedApplications,
  );
  $: commonApplications = (comparison.applications || []).filter(
    (row) => row.x?.application && row.y?.application,
  ).length;
  $: xOnlyApplications = (comparison.applications || []).filter(
    (row) => row.x?.application && !row.y?.application,
  ).length;
  $: yOnlyApplications = (comparison.applications || []).filter(
    (row) => !row.x?.application && row.y?.application,
  ).length;
  $: teamComparisonSignals = buildTeamComparisonSignals(
    comparison,
    commonApplications,
    xOnlyApplications,
    yOnlyApplications,
  );
  $: teamSelectionData = (data.teams || []).map((team) => ({
    id: team,
    clearname: team,
    time: 1,
  }));
  $: teamASelection = xTeams[0] || "";
  $: teamBSelection = yTeams[0] || "";

  onMount(loadAnalysis);

  function emptyAnalysis() {
    return {
      history: [],
      changes: [],
      teams: [],
      summary: {},
      summary_changes: {},
      team_comparison_ready: false,
      team_comparison: {
        x_teams: [],
        y_teams: [],
        x_summary: {},
        y_summary: {},
        applications: [],
      },
    };
  }

  async function loadAnalysis() {
    loading = true;
    error = "";
    try {
      const query = new URLSearchParams();
      if (baselineId) query.set("baseline_id", baselineId);
      if (currentId) query.set("current_id", currentId);
      xTeams.forEach((team) => query.append("x_team", team));
      yTeams.forEach((team) => query.append("y_team", team));
      const response = await fetchWithSession(
        `/api/monitoring/analysis${query.toString() ? `?${query}` : ""}`,
      );
      const result = await response.json();
      if (!response.ok) {
        throw new Error(
          result.message || "Software analysis could not be loaded.",
        );
      }
      data = result;
      baselineId = String(result.selected?.baseline_id || "");
      currentId = String(result.selected?.current_id || "");
      xTeams = result.team_comparison?.x_teams || [];
      yTeams = result.team_comparison?.y_teams || [];
      const availableApplications = new Set(
        (result.team_comparison?.applications || []).flatMap((row) =>
          [row.x?.application, row.y?.application].filter(Boolean),
        ),
      );
      focusedApplications = focusedApplications.filter((application) =>
        availableApplications.has(application),
      );
    } catch (loadError) {
      error = loadError.message || "Software analysis could not be loaded.";
    } finally {
      loading = false;
    }
  }

  function formatDuration(seconds) {
    const hours = Number(seconds || 0) / 3600;
    if (hours >= 1000) {
      return `${new Intl.NumberFormat("en", {
        maximumFractionDigits: 1,
      }).format(hours / 1000)}k h`;
    }
    return `${new Intl.NumberFormat("en", {
      maximumFractionDigits: hours < 10 ? 1 : 0,
    }).format(hours)} h`;
  }

  function formatNumber(value) {
    return new Intl.NumberFormat("en", { notation: "compact" }).format(
      Number(value || 0),
    );
  }

  function formatTimeShare(application, summaryData) {
    const share = getTimeShare(application, summaryData);
    if (share > 0 && share < 1) return "<1%";
    return `${Math.round(share)}%`;
  }

  function formatClickShare(application, summaryData) {
    const share = getClickShare(application, summaryData);
    if (share > 0 && share < 1) return "<1%";
    return `${Math.round(share)}%`;
  }

  function getTimeShare(application, summaryData) {
    const totalActiveTime = Number(summaryData?.active_time_seconds || 0);
    if (!totalActiveTime) return 0;
    return (
      (Number(application?.active_time_seconds || 0) / totalActiveTime) * 100
    );
  }

  function getClickShare(application, summaryData) {
    const totalClicks = Number(summaryData?.clicks || 0);
    if (!totalClicks) return 0;
    return (Number(application?.clicks || 0) / totalClicks) * 100;
  }

  function formatDate(value) {
    if (!value) return "No date";
    return new Intl.DateTimeFormat("en", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(new Date(value));
  }

  function formatChange(value) {
    const number = Number(value || 0);
    return `${number > 0 ? "+" : ""}${number.toFixed(1)}%`;
  }

  function changeTone(value) {
    if (Number(value) > 0) return "positive";
    if (Number(value) < 0) return "negative";
    return "neutral";
  }

  function buildChartData(items, metricKey) {
    if (!items.length) return [];
    const latestApplications = items[items.length - 1].applications || [];
    const applicationNames = [...latestApplications]
      .sort((a, b) => Number(b[metricKey] || 0) - Number(a[metricKey] || 0))
      .slice(0, 5)
      .map((application) => application.application);
    const divisor = metrics[metricKey].chartDivisor;
    return applicationNames.map((applicationName) => ({
      label: applicationName,
      data: items.map((item) => ({
        x: formatDate(item.imported_at),
        y:
          Number(
            item.applications?.find(
              (application) => application.application === applicationName,
            )?.[metricKey] || 0,
          ) / divisor,
      })),
    }));
  }

  function buildDecisionSignals(rows) {
    const signals = [];
    const newApplications = rows.filter((row) => row.status === "new");
    const unwantedGrowth = rows.filter(
      (row) =>
        String(row.type).toLowerCase() === "unwanted" &&
        row.changes.active_time_seconds > 20,
    );
    const copyGrowth = [...rows]
      .filter(
        (row) =>
          row.current.copy_time_seconds > 0 &&
          row.changes.copy_time_seconds > 25,
      )
      .sort(
        (a, b) => b.current.copy_time_seconds - a.current.copy_time_seconds,
      );
    const biggestIncrease = [...rows]
      .filter((row) => row.status === "increased")
      .sort(
        (a, b) =>
          b.current.active_time_seconds -
          b.baseline.active_time_seconds -
          (a.current.active_time_seconds - a.baseline.active_time_seconds),
      )[0];
    const biggestDecrease = [...rows]
      .filter((row) => row.status === "decreased")
      .sort(
        (a, b) =>
          a.current.active_time_seconds -
          a.baseline.active_time_seconds -
          (b.current.active_time_seconds - b.baseline.active_time_seconds),
      )[0];

    if (unwantedGrowth.length) {
      signals.push({
        tone: "risk",
        title: `${unwantedGrowth.length} unwanted app${unwantedGrowth.length === 1 ? "" : "s"} growing`,
        detail: `${unwantedGrowth
          .slice(0, 2)
          .map((row) => row.application)
          .join(
            ", ",
          )} should be checked for policy, alternatives, and support effort.`,
      });
    }
    if (copyGrowth[0]) {
      signals.push({
        tone: "opportunity",
        title: `Copy effort rising in ${copyGrowth[0].application}`,
        detail: `${formatChange(copyGrowth[0].changes.copy_time_seconds)} versus baseline. Inspect interfaces, duplicate entry, and automation potential.`,
      });
    }
    if (newApplications.length) {
      signals.push({
        tone: "information",
        title: `${newApplications.length} new application${newApplications.length === 1 ? "" : "s"} detected`,
        detail:
          "Validate ownership, classification, license fit, and whether existing tools cover the same need.",
      });
    }
    if (biggestIncrease) {
      signals.push({
        tone: "information",
        title: `${biggestIncrease.application} gained most active time`,
        detail: `${formatChange(biggestIncrease.changes.active_time_seconds)} versus baseline. Confirm whether this reflects intended adoption.`,
      });
    }
    if (biggestDecrease) {
      signals.push({
        tone: "opportunity",
        title: `${biggestDecrease.application} usage declined`,
        detail: `${formatChange(biggestDecrease.changes.active_time_seconds)} versus baseline. Check migration progress, access issues, or redundancy.`,
      });
    }
    return signals.slice(0, 5);
  }

  function buildTeamTable(rows = [], side) {
    return rows
      .filter((row) => row[side]?.application)
      .map((row) => ({
        ...row[side],
        onlyOnSide: !row[side === "x" ? "y" : "x"]?.application,
      }))
      .sort(
        (a, b) =>
          Number(b.active_time_seconds || 0) -
          Number(a.active_time_seconds || 0),
      );
  }

  function prioritizeFocusedApplications(applications, focused) {
    return [...applications].sort((a, b) => {
      const aFocused = focused.includes(a.application);
      const bFocused = focused.includes(b.application);
      if (aFocused !== bFocused) return aFocused ? -1 : 1;
      return (
        Number(b.active_time_seconds || 0) - Number(a.active_time_seconds || 0)
      );
    });
  }

  function focusedApplicationsFor(applications) {
    const available = new Set(
      applications.map((application) => application.application),
    );
    return focusedApplications.filter((application) =>
      available.has(application),
    );
  }

  function toggleApplicationFocus(application) {
    focusedApplications = focusedApplications.includes(application)
      ? focusedApplications.filter((item) => item !== application)
      : [...focusedApplications, application];
  }

  function buildTeamComparisonSignals(
    comparisonData,
    commonCount,
    xOnlyCount,
    yOnlyCount,
  ) {
    const xSummary = comparisonData.x_summary || {};
    const ySummary = comparisonData.y_summary || {};
    const xAppsPerUser = xSummary.users
      ? xSummary.application_user_assignments / xSummary.users
      : 0;
    const yAppsPerUser = ySummary.users
      ? ySummary.application_user_assignments / ySummary.users
      : 0;
    return [
      {
        title: "Shared tool core",
        value: `${commonCount} apps`,
        detail: "Applications used on both sides.",
      },
      {
        title: "Portfolio divergence",
        value: `${xOnlyCount} / ${yOnlyCount}`,
        detail: "Tools exclusive to Team A / Team B.",
      },
      {
        title: "Apps per user",
        value: `${xAppsPerUser.toFixed(1)} / ${yAppsPerUser.toFixed(1)}`,
        detail: "Breadth of the tool portfolio per user.",
      },
    ];
  }

  function selectTeam(side, team) {
    if (side === "x") {
      const previousTeam = xTeams[0];
      if (yTeams[0] === team) {
        yTeams = previousTeam ? [previousTeam] : [];
      }
      xTeams = [team];
    } else {
      const previousTeam = yTeams[0];
      if (xTeams[0] === team) {
        xTeams = previousTeam ? [previousTeam] : [];
      }
      yTeams = [team];
    }
    loadAnalysis();
  }

  function toggleTeamPanelFullscreen(side) {
    fullscreenTeamPanel = fullscreenTeamPanel === side ? null : side;
  }

  function handleWindowKeydown(event) {
    if (event.key === "Escape" && fullscreenTeamPanel !== null) {
      fullscreenTeamPanel = null;
    }
  }

  function getTeamTitle(teams, fallback) {
    if (!teams?.length) return fallback;
    if (teams.length <= 2) return teams.join(" + ");
    return `${teams.slice(0, 2).join(" + ")} +${teams.length - 2}`;
  }

  async function requestPaxbotAnswer(question, historyItems, responseLanguage) {
    const response = await fetchWithSession("/api/damprojects/paxbot/chat", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        question,
        history: historyItems,
        context: {
          page: "software change analysis",
          responseLanguage,
          view: "team comparison",
          teamComparison: {
            teamA: xTeams,
            teamB: yTeams,
            xTeams,
            yTeams,
            teamASummary: comparison.x_summary,
            teamBSummary: comparison.y_summary,
            xSummary: comparison.x_summary,
            ySummary: comparison.y_summary,
            applications: (comparison.applications || []).slice(0, 12),
          },
        },
      }),
    });
    const result = await response.json();
    if (!response.ok || result.status === "error") {
      throw new Error(result.message || "Paxbot request failed");
    }
    return result.answer;
  }
</script>

<svelte:window on:keydown={handleWindowKeydown} />

<Header
  section="Digital Twin"
  projectNameOverride="Digital Twin"
  projectHref={ROUTES.dam.root}
  area="Software Intelligence"
  areaClickable={false}
  breadcrumb="Software Change Analysis"
  tooltipText="Software Change Analysis compares transferred data and teams over time. Use it to spot growing or shrinking applications, unwanted tool adoption, changed usage intensity and differences between team groups."
></Header>

{#if sourceMode === 0}
  <SectionWrapper
    fitScreen
    {loading}
    loadingTitle="Loading software change data."
    loadingHint="Please wait while Software Change Analysis is prepared."
    helpContext={softwareChangeAnalysisHelpContext}
  >
    <div class="analysis-main" slot="main">
      <PaxbotChat
        bind:open={paxbotOpen}
        heading="Pax AI"
        subheading="Software Change Analysis Assistant"
        requestAnswer={requestPaxbotAnswer}
        starterQuestions={[
          "What should I investigate first?",
          "Which unwanted apps are growing?",
          "Where do Team A and Team B differ most?",
          "Where is consolidation potential?",
          "Apply the management lens to this comparison.",
        ]}
      />

      {#if loading}
        <DataTableSkeleton
          rows={10}
          headers={[
            { key: "application", value: "Application" },
            { key: "baseline", value: "Baseline" },
            { key: "current", value: "Current" },
            { key: "change", value: "Change" },
          ]}
          showToolbar={false}
        />
      {:else if error}
        <div class="empty-state error">{error}</div>
      {:else if !history.length}
        <div class="empty-state">
          Transfer at least one calculated software result to start the
          analysis.
        </div>
      {:else if analysisView === 0}
        <div class="development-controls">
          <Select
            labelText="Baseline"
            size="sm"
            bind:selected={baselineId}
            on:change={loadAnalysis}
          >
            {#each history as snapshot}
              <SelectItem
                value={String(snapshot.id)}
                text={formatDate(snapshot.imported_at)}
              />
            {/each}
          </Select>
          <Select
            labelText="Comparison"
            size="sm"
            bind:selected={currentId}
            on:change={loadAnalysis}
          >
            {#each history as snapshot}
              <SelectItem
                value={String(snapshot.id)}
                text={formatDate(snapshot.imported_at)}
              />
            {/each}
          </Select>
          <Select labelText="Metric" size="sm" bind:selected={selectedMetric}>
            {#each Object.entries(metrics) as [key, option]}
              <SelectItem value={key} text={option.label} />
            {/each}
          </Select>
        </div>

        <div class="overview-kpis">
          {#each [["Applications", "application_count"], ["Users", "users"], ["Annual active time", "active_time_seconds"], ["Annual copy time", "copy_time_seconds"]] as [label, key]}
            <div class="overview-kpi">
              <span>{label}</span>
              <div>
                <strong>
                  {key.includes("time")
                    ? formatDuration(summary[key])
                    : formatNumber(summary[key])}
                </strong>
                <small class={changeTone(summaryChanges[key])}>
                  {formatChange(summaryChanges[key])}
                </small>
              </div>
            </div>
          {/each}
        </div>

        <section class="trend-panel">
          <div class="panel-heading">
            <div>
              <strong>{metric.label} development</strong>
              <span>Top five applications in the current portfolio</span>
            </div>
            <small
              >{selectedMetric.includes("time") ? "Hours" : metric.label}</small
            >
          </div>
          {#if chartDataSets.length}
            <div class="chart">
              <LineChartNeu
                height="100%"
                width="100%"
                dataSets={chartDataSets}
                axes={chartDataSets.map(() => "y")}
                variant="clean"
                displayLegend
              />
            </div>
          {:else}
            <div class="empty-chart">No development data available.</div>
          {/if}
        </section>

        <section class="change-table-panel">
          <div class="panel-heading">
            <div>
              <strong>Application changes</strong>
              <span>Largest absolute movements appear first</span>
            </div>
            <small>{changes.length} applications</small>
          </div>
          <div class="table-scroll">
            <table>
              <thead>
                <tr>
                  <th>Application</th>
                  <th>Category</th>
                  <th>Type</th>
                  <th>Baseline</th>
                  <th>Current</th>
                  <th>Change</th>
                  <th>Users Δ</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {#each changes as row}
                  <tr>
                    <td>
                      <strong class="application" title={row.application}>
                        {row.application}
                      </strong>
                    </td>
                    <td>{row.category}</td>
                    <td>
                      <span class="type-badge {row.type}">
                        {row.type || "Unclassified"}
                      </span>
                    </td>
                    <td>{metric.formatter(row.baseline[selectedMetric])}</td>
                    <td>{metric.formatter(row.current[selectedMetric])}</td>
                    <td>
                      <span class={changeTone(row.changes[selectedMetric])}>
                        {formatChange(row.changes[selectedMetric])}
                      </span>
                    </td>
                    <td>
                      {row.current.users - row.baseline.users > 0 ? "+" : ""}
                      {row.current.users - row.baseline.users}
                    </td>
                    <td>
                      <span class="status {row.status}">{row.status}</span>
                    </td>
                  </tr>
                {/each}
              </tbody>
            </table>
          </div>
        </section>
      {:else}
        {#if !data.team_comparison_ready}
          <div class="refresh-notice">
            Exact team KPIs require the current calculated data format. Run
            <strong>Precompute caches</strong> and transfer the project again. Existing
            global history remains available.
          </div>
        {/if}

        <div
          class="xy-grid"
          class:has-fullscreen={fullscreenTeamPanel !== null}
        >
          {#each [{ side: "x", label: "Team A", teams: xTeams, applications: xApplications, summary: comparison.x_summary || {} }, { side: "y", label: "Team B", teams: yTeams, applications: yApplications, summary: comparison.y_summary || {} }] as group}
            <section
              class="team-panel {group.side}"
              class:team-panel-fullscreen={fullscreenTeamPanel === group.side}
              class:team-panel-hidden={fullscreenTeamPanel !== null &&
                fullscreenTeamPanel !== group.side}
            >
              <div class="team-panel-heading">
                <div>
                  <span>{group.label}</span>
                  <strong
                    >{getTeamTitle(group.teams, "No team selected")}</strong
                  >
                </div>
                <ButtonIconOnly
                  small
                  icon={fullscreenTeamPanel === group.side
                    ? "fullscreen_off"
                    : "fullscreen"}
                  title={fullscreenTeamPanel === group.side
                    ? "Exit fullscreen"
                    : `Expand ${group.label}`}
                  on:click={() => toggleTeamPanelFullscreen(group.side)}
                />
              </div>
              {#if focusedApplicationsFor(group.applications).length}
                <div class="focused-apps">
                  <span>Focused apps</span>
                  <div>
                    {#each focusedApplicationsFor(group.applications) as application}
                      <button
                        type="button"
                        title={`Remove ${application} from focus`}
                        on:click={() => toggleApplicationFocus(application)}
                      >
                        {application}<span aria-hidden="true">×</span>
                      </button>
                    {/each}
                  </div>
                </div>
              {/if}
              <div class="team-table-scroll">
                <table>
                  <thead>
                    <tr>
                      <th>App</th>
                      <th>Time share</th>
                      <th>Users</th>
                      <th>Click share</th>
                    </tr>
                  </thead>
                  <tbody>
                    {#each group.applications as application}
                      <tr
                        class:exclusive={application.onlyOnSide}
                        class:focused={focusedApplications.includes(
                          application.application,
                        )}
                        role="button"
                        tabindex="0"
                        aria-pressed={focusedApplications.includes(
                          application.application,
                        )}
                        on:click={() =>
                          toggleApplicationFocus(application.application)}
                        on:keydown={(event) => {
                          if (event.key === "Enter" || event.key === " ") {
                            event.preventDefault();
                            toggleApplicationFocus(application.application);
                          }
                        }}
                      >
                        <td>
                          <strong
                            class="application"
                            title={application.application}
                          >
                            {application.application}
                          </strong>
                          {#if application.onlyOnSide}
                            <small>{group.label} only</small>
                          {/if}
                        </td>
                        <td>
                          <span class="metric-share">
                            {formatTimeShare(application, group.summary)}
                            {#if getTimeShare(application, group.summary) > 0}
                              <Tooltip
                                text={`Annual active time: ${formatDuration(application.active_time_seconds)}`}
                              />
                            {/if}
                          </span>
                        </td>
                        <td>{formatNumber(application.users)}</td>
                        <td>
                          <span class="metric-share">
                            {formatClickShare(application, group.summary)}
                            {#if getClickShare(application, group.summary) > 0}
                              <Tooltip
                                text={`Annual clicks: ${formatNumber(application.clicks)}`}
                              />
                            {/if}
                          </span>
                        </td>
                      </tr>
                    {/each}
                    {#if !group.applications.length}
                      <tr>
                        <td colspan="4" class="no-team-data">
                          Select a team with current calculated data.
                        </td>
                      </tr>
                    {/if}
                  </tbody>
                </table>
              </div>
            </section>
          {/each}
        </div>
      {/if}
    </div>

    <div class="analysis-menu" slot="menu">
      {#if analysisView === 0}
        <Accordion heading="Decision Signals" open>
          {#if decisionSignals.length}
            <div class="signal-list">
              {#each decisionSignals as signal}
                <div class="signal {signal.tone}">
                  <span />
                  <div>
                    <strong>{signal.title}</strong>
                    <p>{signal.detail}</p>
                  </div>
                </div>
              {/each}
            </div>
          {:else}
            <p class="menu-note">
              Add another data update to surface directional signals.
            </p>
          {/if}
        </Accordion>

        <Accordion heading="Portfolio Health" open>
          <div class="kpi-list">
            <SideKpi
              name="New applications"
              value={changes.filter((row) => row.status === "new").length}
            />
            <SideKpi
              name="Growing unwanted apps"
              value={changes.filter(
                (row) =>
                  String(row.type).toLowerCase() === "unwanted" &&
                  row.changes.active_time_seconds > 20,
              ).length}
            />
            <SideKpi
              name="Declining applications"
              value={changes.filter((row) => row.status === "decreased").length}
            />
            <SideKpi
              name="Avg. apps per user"
              value={Number(summary.average_apps_per_user || 0).toFixed(1)}
            />
          </div>
        </Accordion>

        <Accordion heading="How to use this view">
          <p class="menu-note">
            Use active time for adoption shifts, users for reach, copy/text time
            for interface friction, and clicks for interaction intensity.
            Validate business causes before acting on an aggregate signal.
          </p>
        </Accordion>
      {:else}
        <Accordion heading="Team A" open>
          <div class="team-selection-content">
            <AppSelection
              appData={teamSelectionData}
              bind:currentApp={teamASelection}
              windowSelectionEnabled={false}
              enableDeselection={false}
              searchPlaceholder="Search Team"
              paginationItem="Teams"
              showShare={false}
              on:select={(event) => selectTeam("x", event.detail)}
            />
          </div>
        </Accordion>

        <Accordion heading="Team B" open>
          <div class="team-selection-content">
            <AppSelection
              appData={teamSelectionData}
              bind:currentApp={teamBSelection}
              windowSelectionEnabled={false}
              enableDeselection={false}
              searchPlaceholder="Search Team"
              paginationItem="Teams"
              showShare={false}
              on:select={(event) => selectTeam("y", event.detail)}
            />
          </div>
        </Accordion>

        <Accordion heading="Comparison Signals" open>
          <div class="kpi-list">
            {#each teamComparisonSignals as signal}
              <SideKpi
                name={signal.title}
                value={signal.value}
                tooltipText={signal.detail}
              />
            {/each}
          </div>
        </Accordion>
      {/if}
    </div>
  </SectionWrapper>
{:else}
  <OnGoingAnalysisPlaceholder
    title="On-going Software Change Analysis"
    description="Follow live adoption shifts, emerging applications and changing team portfolios from continuously incoming results."
  />
{/if}

<style>
  .analysis-main {
    position: relative;
    display: flex;
    width: 100%;
    min-width: 0;
    height: 100%;
    flex-direction: column;
    gap: 0.9rem;
  }

  .menu-note {
    margin: 0.25rem 0 0;
    color: #6f7681;
    font-size: 0.78rem;
    line-height: 1.45;
  }

  .development-controls {
    display: grid;
    flex: 0 0 auto;
    grid-template-columns: repeat(3, minmax(11rem, 1fr));
    gap: 0.65rem;
  }

  .overview-kpis {
    display: grid;
    flex: 0 0 auto;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 0.6rem;
  }

  .overview-kpi {
    display: flex;
    min-width: 0;
    flex-direction: column;
    gap: 0.45rem;
    padding: 0.75rem 0.85rem;
    border: 1px solid #e1e4e8;
    border-radius: 0.5rem;
    background: #fff;
  }

  .overview-kpi > span {
    color: #6f7681;
    font-size: 0.72rem;
  }

  .overview-kpi > div {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 0.5rem;
  }

  .overview-kpi strong {
    font-size: 1.05rem;
  }

  .overview-kpi small,
  .positive {
    color: #198038;
    font-weight: 700;
  }

  .negative {
    color: #da1e28;
    font-weight: 700;
  }

  .neutral {
    color: #6f7681;
    font-weight: 700;
  }

  .trend-panel,
  .change-table-panel,
  .team-panel {
    display: flex;
    min-height: 0;
    flex-direction: column;
    overflow: hidden;
    border: 1px solid var(--pax-black);
    border-radius: 0.5rem;
    background: #fff;
  }

  .trend-panel {
    min-height: 13rem;
    flex: 0.8;
  }

  .change-table-panel {
    min-height: 14rem;
    flex: 1;
  }

  .panel-heading,
  .team-panel-heading {
    display: flex;
    flex: 0 0 auto;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    padding: 0.7rem 0.85rem;
    border-bottom: 1px solid #e6e8eb;
  }

  .panel-heading > div,
  .team-panel-heading > div {
    display: flex;
    min-width: 0;
    flex-direction: column;
    gap: 0.15rem;
  }

  .panel-heading span,
  .panel-heading small,
  .team-panel-heading span,
  .team-panel-heading small {
    color: #6f7681;
    font-size: 0.7rem;
  }

  .chart {
    min-height: 0;
    flex: 1;
    padding: 0.25rem 0.5rem 0;
  }

  .empty-chart {
    display: grid;
    flex: 1;
    place-items: center;
    color: #6f7681;
    font-size: 0.8rem;
  }

  .table-scroll,
  .team-table-scroll {
    min-height: 0;
    flex: 1;
    overflow: auto;
  }

  .team-table-scroll {
    overflow-x: hidden;
  }

  table {
    width: 100%;
    border: 0;
    border-collapse: collapse;
    font-size: 0.76rem;
  }

  th {
    position: sticky;
    z-index: 1;
    top: 0;
    padding: 0.48rem 0.65rem;
    background: var(--pax-black);
    color: var(--pax-white);
    font-size: 0.7rem;
    text-align: left;
    white-space: nowrap;
  }

  td {
    height: 2.3rem;
    padding: 0.42rem 0.65rem;
    border-bottom: 1px solid #e6e8eb;
    white-space: nowrap;
  }

  .metric-share {
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
  }

  tbody tr:last-child td {
    border-bottom: 0;
  }

  tbody tr:hover {
    background: #f7f8fa;
  }

  .application {
    display: block;
    max-width: 11rem;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .type-badge,
  .status {
    display: inline-flex;
    padding: 0.2rem 0.45rem;
    border-radius: 99px;
    background: #e8daff;
    color: #6929c4;
    font-size: 0.66rem;
    text-transform: capitalize;
  }

  .type-badge.main {
    background: #d0e2ff;
    color: #0043ce;
  }

  .type-badge.communication,
  .status.increased {
    background: #defbe6;
    color: #0e6027;
  }

  .type-badge.unwanted,
  .status.removed {
    background: #fff1f1;
    color: #a2191f;
  }

  .status.new {
    background: #edf5ff;
    color: #0043ce;
  }

  .status.decreased {
    background: #fff4ce;
    color: #684e00;
  }

  .status.stable {
    background: #e8e8e8;
    color: #525252;
  }

  .analysis-menu {
    display: flex;
    height: calc(100vh - 6rem);
    flex-direction: column;
    gap: 1rem;
    overflow-y: auto;
    scrollbar-width: none;
  }

  .analysis-menu::-webkit-scrollbar {
    display: none;
  }

  /* Keep the team selectors aligned with the compact header controls. */
  .analysis-menu :global(.search) {
    height: 2.25rem;
    padding: 0 0.65rem;
    border-radius: 0.25rem;
  }

  .analysis-menu :global(.searchicon) {
    height: 1rem;
  }

  .analysis-menu :global(.search input) {
    height: 1.8rem;
    padding-left: 0.6rem;
    font-size: 0.75rem;
  }

  .analysis-menu :global(.appinfo_wrapper) {
    height: 2.25rem;
    border-radius: 0.25rem;
  }

  .analysis-menu :global(.first-element) {
    margin-top: 0.35rem;
  }

  .analysis-menu :global(.last-element) {
    padding-top: 0.35rem;
    margin-bottom: 0.35rem;
  }

  .analysis-menu :global(.pagination_wrapper) {
    gap: 0.35rem;
  }

  .analysis-menu :global(.chevron_wrapper.expand) {
    width: 2.25rem;
    height: 2.25rem;
    border-radius: 0.25rem;
  }

  .analysis-menu :global(.chevron_wrapper.expand svg) {
    width: 0.8rem;
    height: 0.8rem;
  }

  .analysis-menu :global(.pagination) {
    height: 2.25rem;
    padding: 0 0.65rem;
    border-radius: 0.25rem;
    font-size: 0.75rem;
  }

  .team-selection-content {
    margin: -0.5rem 0;
  }

  .team-selection-content :global(.app_selection_wrapper) {
    gap: 0.4rem;
  }

  .team-selection-content :global(.bar_wrapper) {
    gap: 0.35rem;
    padding-bottom: 0.45rem;
  }

  .analysis-menu :global(.kpi_wrapper) {
    height: 2.25rem;
    padding: 0.45rem 0.65rem;
    border-radius: 0.25rem;
  }

  .analysis-menu :global(.kpi_wrapper .left_wrapper) {
    gap: 0.5rem;
  }

  .signal-list,
  .kpi-list {
    display: grid;
    gap: 0.55rem;
  }

  .signal {
    display: grid;
    grid-template-columns: 0.5rem 1fr;
    gap: 0.55rem;
    padding: 0.65rem;
    border-radius: 0.5rem;
    background: var(--pax-smoke);
  }

  .signal > span {
    width: 0.5rem;
    height: 0.5rem;
    margin-top: 0.2rem;
    border-radius: 50%;
    background: #4589ff;
  }

  .signal.risk > span {
    background: #da1e28;
  }

  .signal.opportunity > span {
    background: #24a148;
  }

  .signal strong {
    display: block;
    font-size: 0.75rem;
  }

  .signal p {
    margin: 0.2rem 0 0;
    color: #6f7681;
    font-size: 0.68rem;
    line-height: 1.35;
  }

  .refresh-notice {
    padding: 0.7rem 0.85rem;
    border-left: 0.25rem solid var(--pax-orange);
    border-radius: 0.25rem;
    background: #fff4ef;
    font-size: 0.75rem;
  }

  .xy-grid {
    display: grid;
    min-height: 0;
    flex: 1;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    gap: 0.75rem;
  }

  .xy-grid.has-fullscreen {
    grid-template-columns: minmax(0, 1fr);
  }

  .team-panel {
    min-height: 0;
  }

  .team-panel-fullscreen {
    grid-column: 1 / -1;
  }

  .team-panel-hidden {
    display: none;
  }

  .team-panel-heading strong {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .team-panel td:first-child {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.5rem;
  }

  .team-panel table {
    table-layout: fixed;
  }

  .team-panel th:first-child {
    width: 45%;
  }

  .team-panel th:not(:first-child) {
    width: 18.33%;
  }

  .team-panel tbody tr {
    cursor: pointer;
    transition:
      background 160ms ease,
      box-shadow 160ms ease;
  }

  .team-panel tbody tr:focus-visible {
    outline: 2px solid #0f62fe;
    outline-offset: -2px;
  }

  .team-panel tbody tr.focused {
    background: #eef0f2;
    color: var(--pax-black);
  }

  .team-panel tbody tr.focused > td {
    background-color: #eef0f2 !important;
    border-bottom-color: #d9dde2 !important;
    color: var(--pax-black) !important;
  }

  .team-panel tbody tr.focused > td:first-child {
    box-shadow: inset 0.2rem 0 var(--pax-black);
  }

  .team-panel tbody tr.focused strong,
  .team-panel tbody tr.focused small {
    color: var(--pax-black) !important;
  }

  .team-panel tbody tr.focused small {
    background: #dfe3e7 !important;
  }

  .team-panel td small {
    padding: 0.15rem 0.35rem;
    border-radius: 99px;
    background: #edf5ff;
    color: #0043ce;
    font-size: 0.58rem;
  }

  .team-panel.y td small {
    background: #fff1e6;
    color: #a53b00;
  }

  .exclusive {
    background: #fafcff;
  }

  .focused-apps {
    display: flex;
    min-width: 0;
    flex: 0 0 auto;
    align-items: center;
    gap: 0.55rem;
    padding: 0.45rem 0.65rem;
    overflow: hidden;
    border-bottom: 1px solid #e6e8eb;
    background: #fafafa;
  }

  .focused-apps > span {
    flex: 0 0 auto;
    color: #6f7681;
    font-size: 0.65rem;
    font-weight: 700;
  }

  .focused-apps > div {
    display: flex;
    min-width: 0;
    gap: 0.35rem;
    overflow-x: auto;
    scrollbar-width: none;
  }

  .focused-apps > div::-webkit-scrollbar {
    display: none;
  }

  .focused-apps button {
    display: inline-flex;
    max-width: 10rem;
    flex: 0 0 auto;
    align-items: center;
    gap: 0.35rem;
    padding: 0.25rem 0.45rem;
    overflow: hidden;
    border: 1px solid #d5d7da;
    border-radius: 99px;
    background: #fff;
    font-size: 0.65rem;
    text-overflow: ellipsis;
    white-space: nowrap;
    cursor: pointer;
  }

  .focused-apps button:hover {
    border-color: var(--pax-orange);
  }

  .focused-apps button span {
    font-size: 0.8rem;
    line-height: 1;
  }

  .empty-state {
    display: grid;
    min-height: 15rem;
    flex: 1;
    place-items: center;
    color: #6f7681;
  }

  .empty-state.error {
    color: #da1e28;
  }

  .no-team-data {
    height: 8rem;
    color: #6f7681;
    text-align: center;
  }

  @media (max-width: 1200px) {
    .overview-kpis {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    .xy-grid {
      grid-template-columns: 1fr;
      overflow-y: auto;
    }

    .team-panel {
      min-height: 18rem;
    }
  }
</style>
